/*!
 * 1DS JS SDK Properties plugin, 2.4.7
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
/**
* Utils.ts
* @author  Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
* @copyright Microsoft 2018
* File containing utility functions.
*/
import { CoreUtils, cookieAvailable, LoggingSeverity, _ExtendedInternalMessageId, getLocation, getDocument, getGlobal, disallowsSameSiteNone, getNavigator, isFunction } from '@microsoft/1ds-core-js';
var _canUseLocalStorage = undefined;
var _uaDisallowsSameSiteNone = null;
/**
* Type of storage to differentiate between local storage and session storage
*/
export var StorageType;
(function (StorageType) {
    StorageType[StorageType["LocalStorage"] = 0] = "LocalStorage";
    StorageType[StorageType["SessionStorage"] = 1] = "SessionStorage";
})(StorageType || (StorageType = {}));
/**
    * helper method to set userId and sessionId cookie
    */
export function _setCookie(logger, name, value, domain) {
    var domainAttrib = "";
    var secureAttrib = "";
    var doc = getDocument();
    if (!doc) {
        return;
    }
    if (domain) {
        domainAttrib = ";domain=" + domain;
    }
    var location = getLocation();
    if (location && location.protocol === "https:") {
        secureAttrib = ";secure";
        if (_uaDisallowsSameSiteNone === null) {
            _uaDisallowsSameSiteNone = disallowsSameSiteNone((getNavigator() || {}).userAgent);
        }
        if (!_uaDisallowsSameSiteNone) {
            value = value + ";SameSite=None"; // SameSite can only be changed on secure pages and browsers that support samesite=None setting
        }
    }
    if (cookieAvailable()) {
        doc.cookie = name + "=" + value + domainAttrib + ";path=/" + secureAttrib;
    }
}
/**
     *  Check if the browser supports local storage.
     *
     *  @returns {boolean} True if local storage is supported.
     */
export function canUseLocalStorage() {
    if (_canUseLocalStorage === undefined) {
        _canUseLocalStorage = !!_getVerifiedStorageObject(StorageType.LocalStorage);
    }
    return _canUseLocalStorage;
}
/**
* Gets the localStorage object if available
* @returns Returns the storage object if available else returns null
*/
function _getLocalStorageObject() {
    if (canUseLocalStorage()) {
        return _getVerifiedStorageObject(StorageType.LocalStorage);
    }
    return null;
}
/**
     * Tests storage object (localStorage or sessionStorage) to verify that it is usable
     * More details here: https://mathiasbynens.be/notes/localstorage-pattern
     * @param storageType - Type of storage
     * @returns Returns storage object verified that it is usable
     */
function _getVerifiedStorageObject(storageType) {
    var storage = null;
    var fail;
    var uid;
    try {
        var global = getGlobal();
        if (!global) {
            return null;
        }
        uid = new Date;
        storage = storageType === StorageType.LocalStorage ? global.localStorage : global.sessionStorage;
        if (storage && isFunction(storage.setItem)) {
            storage.setItem(uid, uid);
            fail = storage.getItem(uid) != uid;
            storage.removeItem(uid);
            if (fail) {
                storage = null;
            }
        }
    }
    catch (exception) {
        storage = null;
    }
    return storage;
}
/**
     *  Set the contents of an object in the browser's local storage
     *
     *  @param name - the name of the object to set in storage
     *  @param data - the contents of the object to set in storage
     *  @returns True if the storage object could be written.
     */
export function setStorage(logger, name, data) {
    var storage = _getLocalStorageObject();
    if (storage !== null) {
        try {
            storage.setItem(name, data);
            return true;
        }
        catch (e) {
            _canUseLocalStorage = false;
            logger.throwInternal(LoggingSeverity.CRITICAL, _ExtendedInternalMessageId.BrowserCannotWriteLocalStorage, "Browser failed write to local storage. " + e);
        }
    }
    return false;
}
/**
     *  Get an object from the browser's local storage
     *
     *  @param name - the name of the object to get from storage
     *  @returns The contents of the storage object with the given name. Null if storage is not supported.
     */
export function getStorage(logger, name) {
    var storage = _getLocalStorageObject();
    if (storage !== null) {
        try {
            return storage.getItem(name);
        }
        catch (e) {
            _canUseLocalStorage = false;
            logger.throwInternal(LoggingSeverity.CRITICAL, _ExtendedInternalMessageId.BrowserCannotReadLocalStorage, "Browser failed read of local storage. " + e);
        }
    }
    return null;
}
/**
* Check if an object is of type Date
*/
export var isDate = CoreUtils.isDate;
/**
* Convert a date to I.S.O. format in IE8
*/
export var toISOString = CoreUtils.toISOString;
//# sourceMappingURL=Utils.js.map